import React, { useState } from "react";
import axios from "axios";
import { baseURL } from "./config";
import { useNavigate } from "react-router-dom";
import { clearUserCache } from "./utils/userCache";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiEye, FiEyeOff, FiCamera } from "react-icons/fi";
import { FaUser, FaPlus } from "react-icons/fa";
import PageHeader from "./components/reusable/PageHeader";
import { useTranslation } from "react-i18next";

const AddUserPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    mobileNumber: "",
    name: "",
    password: "",
    image: null,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    const mobileRegex = /^[6-9]\d{9}$/;

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = t("addUser.errors.mobile_req");
    } else if (!mobileRegex.test(formData.mobileNumber)) {
      newErrors.mobileNumber = t("addUser.errors.mobile_valid");
    } else if (/^(\d)\1{9}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = t("addUser.errors.mobile_invalid");
    }

    if (!formData.name.trim()) {
      newErrors.name = t("addUser.errors.name_req");
    } else if (formData.name.length < 3) {
      newErrors.name = t("addUser.errors.name_len");
    } else if (/[^a-zA-Z\s]/.test(formData.name)) {
      newErrors.name = t("addUser.errors.name_chars");
    }

    if (!formData.password) {
      newErrors.password = t("addUser.errors.pass_req");
    } else if (formData.password.length < 4) {
      newErrors.password = t("addUser.errors.pass_len");
    }

    if (formData.image) {
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!allowedTypes.includes(formData.image.type)) {
        newErrors.image = t("addUser.errors.img_type");
      }
      if (formData.image.size > 2 * 1024 * 1024) {
        newErrors.image = t("addUser.errors.img_size");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (errors.image) setErrors({ ...errors, image: "" });

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      setErrors({ ...errors, image: t("addUser.errors.img_type") });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErrors({ ...errors, image: t("addUser.errors.img_size") });
      return;
    }

    setFormData((prev) => ({ ...prev, image: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("mobileNumber", formData.mobileNumber);
      payload.append("name", formData.name);
      payload.append("password", formData.password);
      if (formData.image) payload.append("image", formData.image);

      await axios.post(`${baseURL}/addUser`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      clearUserCache();

      toast.success(t("addUser.toast.success"), {
        autoClose: 1500,
        onClose: () => navigate("/customers"),
      });
    } catch (error) {
      console.error("Error adding user:", error);
      let errorMessage = t("addUser.toast.generic_error");
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-paper pb-20 font-sans text-primary">
      <main className="px-5 pt-5 max-w-lg mx-auto">
        <PageHeader title={t("addUser.title")} />

        <div className="bg-white border border-line rounded-2xl p-6 mt-6 shadow-sm">
          <form onSubmit={handleSubmit}>
            {/* Image Upload */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                {formData.image ? (
                  <img
                    src={URL.createObjectURL(formData.image)}
                    alt="profile preview"
                    className="w-24 h-24 rounded-full object-cover border-[3px] border-paper"
                  />
                ) : (
                  <div className="bg-paper border border-dashed border-line rounded-full w-24 h-24 flex items-center justify-center">
                    <FaUser className="text-line text-3xl" />
                  </div>
                )}
                <label className="absolute bottom-0 right-0 bg-primary rounded-full p-2 shadow-md cursor-pointer border-[2.5px] border-white active:scale-95 transition-transform duration-150">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <FiCamera className="text-white text-[14px]" />
                </label>
              </div>
            </div>
            {errors.image && (
              <p className="text-debit text-[11px] font-bold uppercase tracking-wide mb-4 text-center">
                {errors.image}
              </p>
            )}

            {/* Mobile */}
            <div className="mb-5">
              <label className="block text-[11px] font-bold uppercase tracking-widest text-primary mb-1.5 ml-0.5">
                {t("addUser.labels.mobile")}
              </label>
              <input
                type="text"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                inputMode="numeric"
                className={`w-full px-4 py-3 bg-paper/50 border rounded-xl text-[13.5px] font-medium placeholder-[#6B7280] focus:outline-none focus:ring-1 focus:bg-white transition-all ${
                  errors.mobileNumber
                    ? "border-debit focus:border-debit focus:ring-debit"
                    : "border-line focus:border-primary focus:ring-primary"
                }`}
                placeholder={t("addUser.placeholders.mobile")}
              />
              {errors.mobileNumber && (
                <p className="text-debit text-[11px] font-semibold mt-1.5 ml-0.5">
                  {errors.mobileNumber}
                </p>
              )}
            </div>

            {/* Name */}
            <div className="mb-5">
              <label className="block text-[11px] font-bold uppercase tracking-widest text-primary mb-1.5 ml-0.5">
                {t("addUser.labels.name")}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-paper/50 border rounded-xl text-[13.5px] font-medium placeholder-[#6B7280] focus:outline-none focus:ring-1 focus:bg-white transition-all ${
                  errors.name
                    ? "border-debit focus:border-debit focus:ring-debit"
                    : "border-line focus:border-primary focus:ring-primary"
                }`}
                placeholder={t("addUser.placeholders.name")}
              />
              {errors.name && (
                <p className="text-debit text-[11px] font-semibold mt-1.5 ml-0.5">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="mb-8">
              <label className="block text-[11px] font-bold uppercase tracking-widest text-primary mb-1.5 ml-0.5">
                {t("addUser.labels.password")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-paper/50 border rounded-xl text-[13.5px] font-medium placeholder-[#6B7280] pr-10 focus:outline-none focus:ring-1 focus:bg-white transition-all ${
                    errors.password
                      ? "border-debit focus:border-debit focus:ring-debit"
                      : "border-line focus:border-primary focus:ring-primary"
                  }`}
                  placeholder={t("addUser.placeholders.password")}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-4 text-[#6B7280] hover:text-primary transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
              {errors.password ? (
                <p className="text-debit text-[11px] font-semibold mt-1.5 ml-0.5">
                  {errors.password}
                </p>
              ) : (
                <p className="text-[#6B7280] text-[10.5px] mt-1.5 ml-0.5">
                  {t("addUser.labels.hint")}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3.5 mt-2 rounded-[18px] font-slab font-bold text-white text-[15px] tracking-wide active:scale-[0.98] transition-all duration-150 ${
                isSubmitting
                  ? "bg-primary/70 cursor-not-allowed"
                  : "bg-primary shadow-sm"
              }`}
            >
              {isSubmitting
                ? t("addUser.buttons.creating")
                : t("addUser.buttons.create")}
            </button>
          </form>
        </div>
      </main>
      <ToastContainer
        position="bottom-center"
        autoClose={2000}
        hideProgressBar
        theme="colored"
      />
    </div>
  );
};

export default AddUserPage;